import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ---- Sub Category ----
export interface SubCategoryRow {
  id: string;
  parent_id: string;
  name: string;
  name_kr?: string;
  description?: string;
  display_order?: number;
  created_at?: string;
}

// ---- Type matching the san_pham_han_quoc table ----
export interface SanPhamHanQuoc {
  id: string;
  name: string;
  name_kr?: string;
  category_id?: string;
  category_name?: string;
  category_name_kr?: string;
  parent_category_id?: string;
  sub_category_id?: string;
  sub_category_name?: string;
  sub_category_name_kr?: string;
  company_name?: string;
  company_code?: string;
  company_logo?: string;
  manager_code?: string;
  manager_phone?: string;
  manager_email?: string;
  price?: string;
  description?: string;
  description_kr?: string;
  description_detail?: string;
  description_detail_kr?: string;
  images?: string[];
  registered_date?: string;
  tags?: string[];
  tags_kr?: string[];
  is_featured?: boolean;
  original_link?: string;
  card_link?: string;
  hero_tagline?: string;
  hero_tagline_kr?: string;
  display_mode?: string;
  pdf_url?: string;
  pdf_pages?: unknown[];
  pdf_preview_images?: string[];
  external_url?: string;
  builder_pages?: unknown[];
  catalog_builder_pages?: unknown[];
  flipbook_images?: string[];
  media_items?: { url: string; type: string; is_cover?: boolean }[];
  specs?: { label: string; value: string }[];
  specs_kr?: { label: string; value: string }[];
  patents_certifications?: PatentCertification[];
  applications?: {
    title: string;
    description?: string;
    image_url?: string;
  }[];
  company_id?: string;
  sections?: {
    title: string;
    content: string;
    image: string;
    imagePosition: "left" | "right";
    accent?: string;
  }[];
  created_at?: string;
  updated_at?: string;
}

export interface PatentCertification {
  title: string;
  description?: string;
  image_url?: string;
  type: "patent" | "certification" | "award";
}

// ---- Type matching the companies table ----
export interface CompanyExtraLink {
  label: string;
  url: string;
}

export interface CompanyPatent {
  title: string;
  description?: string;
  image_url?: string;
  type: "patent" | "certification" | "award";
}

export interface CompanyRow {
  id: string;
  name: string;
  name_kr?: string;
  tagline_vi?: string;
  tagline_kr?: string;
  description_vi?: string;
  description_kr?: string;
  logo_initials?: string;
  logo_icon?: string;
  logo_from?: string;
  logo_to?: string;
  logo_url?: string;
  founded?: string;
  location?: string;
  employees?: string;
  main_category_name?: string;
  main_category_name_kr?: string;
  category_id?: string;
  website?: string;
  images?: string[];
  extra_links?: CompanyExtraLink[];
  patents_certifications?: CompanyPatent[];
  created_at?: string;
  updated_at?: string;
}

// ---- Event Popup ----
export interface EventPopup {
  id: string;
  title_vi: string;
  title_kr?: string;
  description_vi?: string;
  description_kr?: string;
  image_url?: string;
  button_text_vi?: string;
  button_text_kr?: string;
  button_link?: string;
  bg_color?: string;
  is_active?: boolean;
  show_delay_seconds?: number;
  show_from?: string | null;
  show_until?: string | null;
  design_type?: string;
  event_date?: string;
  event_location_vi?: string;
  event_location_kr?: string;
  highlights?: string[];
  created_at?: string;
  updated_at?: string;
}

// ---- Korean Brief ----
export interface KoreanBriefRow {
  id: string;
  title: string;
  subtitle?: string;
  content?: string;
  image_url?: string;
  gallery?: string[];
  source_name?: string;
  source_url?: string;
  category?: string;
  tags?: string[];
  author?: string;
  is_featured?: boolean;
  is_published?: boolean;
  published_at?: string;
  created_at?: string;
  updated_at?: string;
}

// ---- Category ----
export interface CategoryRow {
  id: string;
  name: string;
  name_kr?: string;
  icon?: string;
  description?: string;
  color?: string;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

// ---- Association Activity ----
export interface AssociationActivityRow {
  id: string;
  title: string;
  title_kr?: string;
  subtitle?: string;
  content?: string;
  image_url?: string;
  gallery?: string[];
  link_url?: string;
  category?: string;
  tags?: string[];
  author?: string;
  is_featured?: boolean;
  is_published?: boolean;
  published_at?: string;
  event_date?: string;
  event_location?: string;
  focal_point_x?: number;
  focal_point_y?: number;
  created_at?: string;
  updated_at?: string;
}

// ---- Hero Banner ----
export interface HeroBannerRow {
  id: string;
  image_url: string;
  slogan_vi: string;
  slogan_kr: string;
  sub_vi?: string;
  sub_kr?: string;
  display_order: number;
  is_active?: boolean;
  // Visual editor fields
  slogan_color?: string;
  slogan_font_family?: string;
  slogan_font_size?: number;
  slogan_white_space?: string;
  slogan_x?: number;
  slogan_y?: number;
  slogan_letter_spacing?: number;
  slogan_line_height?: number;
  image_object_fit?: string;
  image_object_position?: string;
  created_at?: string;
  updated_at?: string;
}

// ---- Inquiry ----
export interface InquiryRow {
  id?: number;
  product_id?: string;
  product_name?: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  message?: string;
  status?: "new" | "read" | "replied" | "closed";
  note?: string;
  created_at?: string;
  updated_at?: string;
}

// ---- Technical Cooperation ----
export interface TechnicalCooperationRow {
  id?: string;
  title: string;
  category?: string;
  content?: string;
  image_url?: string;
  created_at?: string;
  updated_at?: string;
}

// ---- Introduction Pages ----
export interface IntroductionPageRow {
  id: string;
  slug: string;
  title_vi: string;
  title_kr?: string;
  content_vi?: string;
  content_kr?: string;
  is_published?: boolean;
  created_at?: string;
  updated_at?: string;
}
